/**
 * EmailEditProposalSection — the Merlin edit proposal shown ON the email.
 *
 * A customer's change request (revision email) that Merlin translated into a
 * confirmable old→new card used to be reachable only by opening the order.
 * Owner direction (review 0vlvv8): show it in the email itself, the same way
 * a linked order is shown. So:
 *
 * - While the proposal is still `proposed`, this renders the SAME
 *   MerlinEditProposalCard the order modal uses (Confirm & send to Sage /
 *   Adjust values / Dismiss) inline in the opened email, with an "Open order"
 *   link. The full proposal + the order's lines are fetched from the order
 *   detail (GET /portal/orders/{id}), so the card behaves identically here.
 * - Once settled (confirmed / dismissed / expired) it renders a compact status
 *   line so the email still explains what happened to the request.
 *
 * Nothing is ever applied without the CSR's confirm — the card's own guard.
 */
import { useCallback, useEffect, useState } from 'react';

import {
  portalOrderService,
  type OrderEditProposalRead,
} from '../../services/portalOrders';
import type { PortalEmailEditProposal } from '../../types/portal';
import { formatDate, formatRelativeTime } from '../../utils/dateFormatters';
import MerlinEditProposalCard from './MerlinEditProposalCard';
import styles from './EmailEditProposalSection.module.css';

export interface EmailEditProposalSectionProps {
  editProposal: PortalEmailEditProposal | null | undefined;
  /** Opens the order detail modal on the proposal's order. */
  onOpenOrder?: (orderId: string) => void;
  /** Fired after the card confirms/dismisses so the parent refetches the email. */
  onChanged?: () => void;
}

type LineItemRef = { id: string; item_code: string; item_description?: string };

function settledLine(p: PortalEmailEditProposal): string {
  const when = p.responded_at ? ` ${formatRelativeTime(p.responded_at)}` : '';
  switch (p.status) {
    case 'confirmed': {
      // A revision confirmed onto a portal DRAFT (kanban 3db03d35): applied
      // to the draft record, no Sage write — Sage gets it on submission.
      if (p.applied_to_draft)
        return `Confirmed${when} and applied to the draft — it goes to Sage when the draft is submitted.`;
      // "Applied" only once Sage confirmed the write (owner ask: our system
      // must see the change in Sage, not just that we sent it).
      const ws = p.sage_write_status ?? null;
      if (ws === 'confirmed') return `Confirmed${when} and applied in Sage.`;
      if (ws === 'failed')
        return `Confirmed${when}, but the push to Sage failed — flagged for follow-up; not applied in Sage yet.`;
      return `Confirmed${when} and sent to Sage — waiting for Sage to confirm it landed.`;
    }
    case 'dismissed':
      return `Dismissed by a CSR${when}${p.dismiss_reason ? `: “${p.dismiss_reason}”` : ''} — nothing was applied.`;
    case 'expired':
      if (p.expired_reason === 'already_applied' && p.sales_site_ship_date) {
        const sent = p.sales_site_notice_at ? ` ${formatRelativeTime(p.sales_site_notice_at)}` : '';
        return `Closed${when}: the sales site already scheduled ship date ${formatDate(
          p.sales_site_ship_date,
        )} and sent the customer the confirmation${sent}, so nothing was left to apply. Sage's required-by date was not changed.`;
      }
      if (p.expired_reason === 'already_applied')
        return `Closed${when}: Sage already shows this change (keyed by hand), so nothing was left to apply.`;
      if (p.expired_reason === 'answered_by_desk')
        return `Closed${when}: the desk answered the customer on this thread by email; the note had no Sage field to apply.`;
      return `Closed${when}: the order left Sage before anyone acted — this change was never applied.`;
    default:
      return `Proposal is ${p.status}.`;
  }
}

export default function EmailEditProposalSection({
  editProposal,
  onOpenOrder,
  onChanged,
}: EmailEditProposalSectionProps) {
  const [proposal, setProposal] = useState<OrderEditProposalRead | null>(null);
  const [lineItems, setLineItems] = useState<LineItemRef[]>([]);
  // The target is still a portal DRAFT (kanban 3db03d35): the card's confirm
  // applies to the draft instead of pushing to Sage, and says so.
  const [targetIsDraft, setTargetIsDraft] = useState(false);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'ready' | 'gone' | 'error'>(
    'idle',
  );
  // Settled by the card in THIS session — the email item is stale until the
  // parent refetches, so show the outcome immediately instead of a blank.
  const [localOutcome, setLocalOutcome] = useState<string | null>(null);

  const pendingId = editProposal?.status === 'proposed' ? editProposal.id : null;
  const orderId = editProposal?.order_id ?? null;

  const fetchProposal = useCallback(async () => {
    if (!pendingId || !orderId) return;
    setLoadState('loading');
    try {
      const detail = await portalOrderService.getOrder(orderId);
      const found = (detail.edit_proposals ?? []).find((p) => p.id === pendingId) ?? null;
      setTargetIsDraft(detail.order.status === 'draft' && !detail.order.sage_order_no);
      setLineItems(
        detail.line_items.map((li) => ({
          id: li.id,
          item_code: li.item_code,
          item_description: li.item_description,
        })),
      );
      setProposal(found);
      setLoadState(found ? 'ready' : 'gone');
    } catch (err) {
      console.error('Failed to load the Merlin edit proposal for this email:', err);
      setLoadState('error');
    }
  }, [pendingId, orderId]);

  useEffect(() => {
    setLocalOutcome(null);
    void fetchProposal();
  }, [fetchProposal]);

  if (!editProposal || !orderId) return null;

  const orderLabel = editProposal.sage_order_no ?? 'the order';
  const openOrderBtn = onOpenOrder ? (
    <button
      type="button"
      className={styles.openOrder}
      onClick={() => onOpenOrder(orderId)}
      data-testid="email-proposal-open-order"
      data-action="order_open"
      data-target-order-id={orderId}
      data-outcome="open"
    >
      Open order {editProposal.sage_order_no ?? ''}
    </button>
  ) : null;

  // ---- settled (from the server, or just now in this session) ------------
  if (!pendingId || localOutcome) {
    return (
      <div
        className={`${styles.section} ${styles.settled}`}
        data-testid="email-edit-proposal"
        data-state="settled"
      >
        <div className={styles.settledRow}>
          <span className={styles.settledIcon} aria-hidden="true">
            ✎
          </span>
          <span className={styles.settledText}>
            <strong>Customer change on {orderLabel}:</strong>{' '}
            {localOutcome ?? settledLine(editProposal)}
          </span>
          {openOrderBtn}
        </div>
        <div className={styles.summary}>{editProposal.summary}</div>
      </div>
    );
  }

  // ---- pending ----------------------------------------------------------
  return (
    <div
      className={`${styles.section} ${editProposal.is_aging ? styles.aging : ''}`}
      data-testid="email-edit-proposal"
      data-state="pending"
    >
      <div className={styles.header}>
        <span className={styles.title}>
          Customer change to order {orderLabel}
          {editProposal.is_aging && (
            <span
              className={styles.agingPill}
              data-testid="email-proposal-aging"
              title="Un-answered for a business day or more while the order is still open in Sage — it ships without this change unless someone acts"
            >
              waiting {editProposal.business_days_open} business day
              {editProposal.business_days_open === 1 ? '' : 's'}
            </span>
          )}
        </span>
        {openOrderBtn}
      </div>

      {loadState === 'loading' && (
        <div className={styles.note} data-testid="email-proposal-loading">
          Loading the proposed changes…
        </div>
      )}
      {loadState === 'error' && (
        <div className={styles.note} role="alert">
          Couldn’t load the proposed changes here — open the order to review them.
        </div>
      )}
      {loadState === 'gone' && (
        <div className={styles.note} data-testid="email-proposal-gone">
          This proposal was already answered — open the order for the current state.
        </div>
      )}
      {loadState === 'ready' && proposal && (
        <MerlinEditProposalCard
          orderId={orderId}
          proposal={proposal}
          lineItems={lineItems}
          targetIsDraft={targetIsDraft}
          onResolved={() => {
            setLocalOutcome('Answered just now — the order reflects your decision.');
            onChanged?.();
          }}
        />
      )}
    </div>
  );
}
